import type { TFunction } from 'i18next';

const MIME_WILDCARD_TRANSLATIONS: Partial<
  Record<string, (t: TFunction<'ui'>) => string>
> = {
  'image/*': (t) => t(($) => $.uploader.restrictionsHint.wildcardImage),
};

export function formatFileType(type: string, t: TFunction<'ui'>): string {
  if (MIME_WILDCARD_TRANSLATIONS[type]) {
    return MIME_WILDCARD_TRANSLATIONS[type](t);
  }

  if (type.startsWith('.')) {
    return type.replace('.', '').toUpperCase();
  }

  if (type.includes('/')) {
    const [, subtype] = type.split('/');

    return subtype === '*'
      ? type.split('/')[0]
      : subtype
        ? subtype.toUpperCase()
        : type;
  }

  return type.toUpperCase();
}

const WILDCARD_EXTENSIONS: Partial<Record<string, readonly string[]>> = {
  'image/*': [
    '.apng',
    '.avif',
    '.bmp',
    '.gif',
    '.heic',
    '.heif',
    '.jpeg',
    '.jpg',
    '.png',
    '.svg',
    '.tif',
    '.tiff',
    '.webp',
  ],
};

/** Filename suffixes a specifier stands for, so a typeless file can still match it. */
function extensionsFor(specifier: string): readonly string[] {
  if (specifier.startsWith('.')) {
    return [specifier];
  }

  const wildcard = WILDCARD_EXTENSIONS[specifier];

  if (wildcard) {
    return wildcard;
  }

  const [, subtype] = specifier.split('/');

  return subtype && subtype !== '*' ? [`.${subtype}`] : [];
}

/**
 * Whether a file satisfies an `accept` list, using the three specifier forms the
 * HTML `accept` attribute defines: a `.ext` suffix, a `type/*` wildcard, or an
 * exact MIME type. An empty list places no restriction.
 *
 * Some sources hand over a file the browser cannot type at all. Matching those
 * on MIME alone would bounce valid uploads, so a typeless file is matched on the
 * filename suffixes each specifier stands for instead.
 */
export function isFileAccepted(file: File, accept: readonly string[]): boolean {
  if (accept.length === 0) {
    return true;
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  if (!fileType) {
    return accept.some((entry) =>
      extensionsFor(entry.trim().toLowerCase()).some((extension) =>
        fileName.endsWith(extension),
      ),
    );
  }

  return accept.some((entry) => {
    const specifier = entry.trim().toLowerCase();

    if (!specifier) {
      return false;
    }

    if (specifier.startsWith('.')) {
      return fileName.endsWith(specifier);
    }

    if (specifier.endsWith('/*')) {
      return fileType.startsWith(specifier.slice(0, -1));
    }

    return fileType === specifier;
  });
}
